import { forwardRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { bubbleVariants, motionSafe, SPRING } from "./motion";
import { useTypewriter } from "./use-typewriter";
import dgptLogo from "@/assets/dgpt.png";

/**
 * The assistant's avatar. Breathes gently while a line is being typed so the
 * chat feels attended-to rather than static.
 */
function AssistantAvatar({ active }: { active: boolean }) {
  const shouldReduce = useReducedMotion();

  return (
    <motion.div
      className="relative mt-0.5 h-7 w-7 shrink-0 rounded-full bg-zinc-950 p-1 ring-1 ring-black/5"
      animate={
        active && !shouldReduce
          ? { scale: [1, 1.06, 1] }
          : { scale: 1 }
      }
      transition={
        active && !shouldReduce
          ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
          : SPRING
      }
    >
      <img src={dgptLogo} alt="" className="h-full w-full object-contain" />
      {active && !shouldReduce && (
        <motion.span
          className="absolute inset-0 rounded-full ring-2 ring-primary/40"
          initial={{ opacity: 0.6, scale: 1 }}
          animate={{ opacity: 0, scale: 1.7 }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
        />
      )}
    </motion.div>
  );
}

/**
 * Ref-forwarded because AnimatePresence's `popLayout` mode measures its
 * children through a ref — a plain function component silently breaks the
 * sibling reflow during an exit.
 */
export const AssistantBubble = forwardRef<
  HTMLDivElement,
  {
    text: string;
    /** Replayed from a saved transcript — render fully, don't re-type. */
    instant?: boolean;
    onDone?: () => void;
  }
>(function AssistantBubble({ text, instant = false, onDone }, ref) {
  const shouldReduce = useReducedMotion();
  const { displayed, isTyping } = useTypewriter({ text, instant, onDone });

  return (
    <motion.div
      ref={ref}
      className="flex w-full gap-3"
      variants={motionSafe(bubbleVariants, shouldReduce)}
      initial="hidden"
      animate="visible"
      layout="position"
    >
      <AssistantAvatar active={isTyping} />
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground">
          {displayed}
          {isTyping && (
            <motion.span
              className="ml-0.5 inline-block h-[1.05em] w-[2px] translate-y-[0.15em] bg-primary"
              animate={{ opacity: [1, 0.15, 1] }}
              transition={{ duration: 0.9, repeat: Infinity }}
            />
          )}
        </p>
      </div>
    </motion.div>
  );
});

/**
 * The user's own answer, echoed back. Answers given through a widget (a picked
 * option, a set of criteria) come through here as a short summary so the
 * transcript reads as a conversation rather than a form log.
 */
export const UserBubble = forwardRef<HTMLDivElement, { text: string }>(
  function UserBubble({ text }, ref) {
  const shouldReduce = useReducedMotion();

  return (
    <motion.div
      ref={ref}
      className="flex w-full justify-end"
      variants={motionSafe(bubbleVariants, shouldReduce)}
      initial="hidden"
      animate="visible"
      layout="position"
    >
      <div
        className={cn(
          "max-w-[85%] rounded-2xl rounded-br-md bg-primary px-4 py-2.5",
          "text-[15px] leading-relaxed text-primary-foreground shadow-sm"
        )}
      >
        <p className="whitespace-pre-wrap">{text}</p>
      </div>
    </motion.div>
  );
  }
);

/** Three-dot "thinking" state, shown while an API call is in flight. */
export function TypingIndicator({ label }: { label?: string }) {
  const shouldReduce = useReducedMotion();

  return (
    <motion.div
      className="flex w-full gap-3"
      variants={motionSafe(bubbleVariants, shouldReduce)}
      initial="hidden"
      animate="visible"
      exit="exit"
      layout="position"
    >
      <AssistantAvatar active />
      <div className="flex items-center gap-2.5 pt-1.5">
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60"
              animate={shouldReduce ? {} : { y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
              transition={{
                duration: 0.9,
                repeat: Infinity,
                delay: i * 0.14,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
        {label && (
          <motion.span
            className="text-sm text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {label}
          </motion.span>
        )}
      </div>
    </motion.div>
  );
}
